import {
  type Question,
  QuestionType,
  TextInputType,
} from "@/commonTypes/question";
import { TransitionBlockType } from "@/commonTypes/transitionContentBlocks";
/**
 * Mock questionnaire data with translation keys.
 * In production, this data would come from an API and be transformed
 * to use translation keys from the en-us.json file.
 */
export const mockQuestionnaire: Question[] = [
  {
    questionType: QuestionType.TEXT,
    questionId: "user-name",
    questionTranslationKey: "questions.user-name.question",
    answers: [
      {
        id: "first-name",
        translationKey: "questions.user-name.answers.first-name.label",
        inputType: TextInputType.TEXT,
        validationType: "name",
      },
      {
        id: "last-name",
        translationKey: "questions.user-name.answers.last-name.label",
        inputType: TextInputType.TEXT,
        validationType: "name",
      },
    ],
  },
  {
    questionType: QuestionType.TEXT,
    questionId: "email",
    questionTranslationKey: "questions.email.question",
    answers: [
      {
        id: "email",
        translationKey: "questions.email.answers.email.label",
        inputType: TextInputType.EMAIL,
        validationType: "email",
      },
    ],
  },
  {
    questionType: QuestionType.TEXT,
    questionId: "phone",
    questionTranslationKey: "questions.phone.question",
    answers: [
      {
        id: "phone",
        translationKey: "questions.phone.answers.phone.label",
        inputType: TextInputType.PHONE,
        validationType: "phone",
        optional: true,
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "how-many-hours-sleep",
    questionTranslationKey: "questions.how-many-hours-sleep.question",
    answers: [
      {
        id: "less-than-five",
        translationKey: "questions.how-many-hours-sleep.answers.less-than-five",
      },
      {
        id: "five-to-seven",
        translationKey: "questions.how-many-hours-sleep.answers.five-to-seven",
      },
      {
        id: "seven-to-nine",
        translationKey: "questions.how-many-hours-sleep.answers.seven-to-nine",
      },
      {
        id: "more-than-nine",
        translationKey: "questions.how-many-hours-sleep.answers.more-than-nine",
      },
    ],
  },
  {
    questionType: QuestionType.TEXT,
    questionId: "height",
    questionTranslationKey: "questions.height.question",
    unitSwitcher: "height",
    answers: [
      {
        id: "feet",
        translationKey: "questions.height.answers.feet.label",
        inputType: TextInputType.TEXT,
        validationType: "height_ft",
      },
      {
        id: "inches",
        translationKey: "questions.height.answers.inches.label",
        inputType: TextInputType.TEXT,
        validationType: "height_in",
      },
      {
        id: "centimeters",
        translationKey: "questions.height.answers.centimeters.label",
        inputType: TextInputType.TEXT,
        validationType: "height_cm",
      },
    ],
  },
  {
    questionType: QuestionType.TEXT,
    questionId: "weight",
    questionTranslationKey: "questions.weight.question",
    unitSwitcher: "weight",
    answers: [
      {
        id: "weight-lbs",
        translationKey: "questions.weight.answers.weight-lbs.label",
        inputType: TextInputType.TEXT,
        validationType: "weight_lbs",
      },
      {
        id: "weight-kg",
        translationKey: "questions.weight.answers.weight-kg.label",
        inputType: TextInputType.TEXT,
        validationType: "weight_kg",
      },
    ],
  },
  {
    questionType: QuestionType.DATE_PICKER,
    questionId: "birth-year",
    questionTranslationKey: "questions.birth-year.question",
    translationKey: "questions.birth-year.answers.label",
    infoToastTranslationKey: "questions.birth-year.info",
    infoToastSourceKey: "questions.birth-year.info-source",
    infoToastVariants: [
      {
        variantKey: "female",
        infoToastTranslationKey: "questions.birth-year.female.info",
        infoToastSourceKey: "questions.birth-year.female.info-source",
        conditions: [{ questionId: "sex-at-birth", answerId: "female" }],
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "user-gender",
    questionTranslationKey: "questions.user-gender.question",
    answers: [
      {
        id: "female",
        translationKey: "questions.user-gender.answers.female",
      },
      {
        id: "male",
        translationKey: "questions.user-gender.answers.male",
      },
      {
        id: "non-binary",
        translationKey: "questions.user-gender.answers.non-binary",
      },
      {
        id: "other",
        translationKey: "questions.user-gender.answers.other",
      },
      {
        id: "prefer-not-to-mention",
        translationKey: "questions.user-gender.answers.prefer-not-to-mention",
      },
    ],
    infoToastTranslationKey: "questions.user-gender.info",
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "sex-at-birth",
    questionTranslationKey: "questions.sex-at-birth.question",
    answers: [
      {
        id: "male",
        translationKey: "questions.sex-at-birth.answers.male",
      },
      {
        id: "female",
        translationKey: "questions.sex-at-birth.answers.female",
      },
      {
        id: "prefer-not-to-mention",
        translationKey: "questions.sex-at-birth.answers.prefer-not-to-mention",
      },
    ],
  },
  {
    questionType: QuestionType.TRANSITION,
    questionId: "talk-about-sleep",
    questionTranslationKey: "transitions.talk-about-sleep.title",
    contentBlocks: [
      {
        type: TransitionBlockType.RICH_TEXT,
        translationKey: "transitions.talk-about-sleep.body",
      },
    ],
    variants: [
      {
        variantKey: "female",
        questionTranslationKey: "transitions.talk-about-sleep.female.title",
        contentBlocks: [
          {
            type: TransitionBlockType.RICH_TEXT,
            translationKey: "transitions.talk-about-sleep.female.body",
          },
        ],
        conditions: [{ questionId: "sex-at-birth", answerId: "female" }],
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "sleep-quality",
    questionTranslationKey: "questions.sleep-quality.question",
    answers: [
      {
        id: "great",
        translationKey: "questions.sleep-quality.answers.great",
      },
      { id: "deep", translationKey: "questions.sleep-quality.answers.deep" },
      { id: "light", translationKey: "questions.sleep-quality.answers.light" },
      {
        id: "could-be-better",
        translationKey: "questions.sleep-quality.answers.could-be-better",
      },
      {
        id: "disturbed",
        translationKey: "questions.sleep-quality.answers.disturbed",
      },
      {
        id: "terrible",
        translationKey: "questions.sleep-quality.answers.terrible",
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "sleep-difficulties",
    questionTranslationKey: "questions.sleep-difficulties.question",
    allowMultipleAnswers: true,
    answers: [
      {
        id: "difficulty-falling-asleep",
        translationKey:
          "questions.sleep-difficulties.answers.difficulty-falling-asleep",
      },
      {
        id: "difficulty-staying-asleep",
        translationKey:
          "questions.sleep-difficulties.answers.difficulty-staying-asleep",
      },
      {
        id: "wake-up-during-night",
        translationKey:
          "questions.sleep-difficulties.answers.wake-up-during-night",
      },
      {
        id: "wake-up-earlier",
        translationKey: "questions.sleep-difficulties.answers.wake-up-earlier",
      },
      {
        id: "none",
        translationKey: "questions.sleep-difficulties.answers.none",
        exclusive: true,
      },
    ],
  },
  {
    questionType: QuestionType.TRANSITION,
    questionId: "sleep-quality-stats",
    questionTranslationKey: "transitions.sleep-quality-stats.title",
    overlineTranslationKey: "transitions.sleep-quality-stats.overline",
    contentBlocks: [
      {
        type: TransitionBlockType.IMAGE,
        imageKey: "tired-man",
        altTranslationKey: "transitions.sleep-quality-stats.image-alt",
      },
      {
        type: TransitionBlockType.SOURCE,
        translationKey: "transitions.sleep-quality-stats.source",
      },
    ],
    variants: [
      {
        variantKey: "female",
        questionTranslationKey: "transitions.sleep-quality-stats.female.title",
        contentBlocks: [
          {
            type: TransitionBlockType.IMAGE,
            imageKey: "woman-looking-at-computer",
            altTranslationKey:
              "transitions.sleep-quality-stats.female.image-alt",
          },
          {
            type: TransitionBlockType.SOURCE,
            translationKey: "transitions.sleep-quality-stats.female.source",
          },
        ],
        conditions: [{ questionId: "sex-at-birth", answerId: "female" }],
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "told-you-snore",
    questionTranslationKey: "questions.told-you-snore.question",
    answers: [
      {
        id: "yes",
        translationKey: "questions.told-you-snore.answers.yes",
      },
      {
        id: "no",
        translationKey: "questions.told-you-snore.answers.no",
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "how-audible-snore",
    questionTranslationKey: "questions.how-audible-snore.question",
    answers: [
      {
        id: "barely-audible",
        translationKey: "questions.how-audible-snore.answers.barely-audible",
      },
      {
        id: "can-be-heard-in-same-room",
        translationKey:
          "questions.how-audible-snore.answers.can-be-heard-in-same-room",
      },
      {
        id: "can-be-heard-in-next-room",
        translationKey:
          "questions.how-audible-snore.answers.can-be-heard-in-next-room",
      },
      {
        id: "can-be-heard-down-the-hall",
        translationKey:
          "questions.how-audible-snore.answers.can-be-heard-down-the-hall",
      },
      {
        id: "dont-know",
        translationKey: "questions.how-audible-snore.answers.dont-know",
      },
    ],
    conditionalOn: [
      {
        condition: "told-you-snore",
        values: ["yes"],
      },
      {
        condition: "sex-at-birth",
        values: ["female"],
        negate: true,
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "hold-breath-sleeping",
    questionTranslationKey: "questions.hold-breath-sleeping.question",
    conditionalOn: [
      {
        condition: "sex-at-birth",
        values: ["female"],
        negate: true,
      },
    ],
    answers: [
      {
        id: "yes",
        translationKey: "questions.hold-breath-sleeping.answers.yes",
      },
      {
        id: "no",
        translationKey: "questions.hold-breath-sleeping.answers.no",
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "high-blood-pressure",
    questionTranslationKey: "questions.high-blood-pressure.question",
    answers: [
      {
        id: "yes",
        translationKey: "questions.high-blood-pressure.answers.yes",
      },
      {
        id: "no",
        translationKey: "questions.high-blood-pressure.answers.no",
      },
      {
        id: "prefer-not-to-say",
        translationKey:
          "questions.high-blood-pressure.answers.prefer-not-to-say",
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "wake-up-with-symptoms",
    questionTranslationKey: "questions.wake-up-with-symptoms.question",
    allowMultipleAnswers: true,
    answers: [
      {
        id: "headaches",
        translationKey: "questions.wake-up-with-symptoms.answers.headaches",
      },
      {
        id: "dry-mouth",
        translationKey: "questions.wake-up-with-symptoms.answers.dry-mouth",
      },
      {
        id: "aching-jaw",
        translationKey: "questions.wake-up-with-symptoms.answers.aching-jaw",
      },
      {
        id: "none-of-the-above",
        translationKey:
          "questions.wake-up-with-symptoms.answers.none-of-the-above",
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "trouble-sleeping-pain",
    questionTranslationKey: "questions.trouble-sleeping-pain.question",
    conditionalOn: [
      {
        condition: "sex-at-birth",
        values: ["female"],
      },
    ],
    answers: [
      {
        id: "never",
        translationKey: "questions.trouble-sleeping-pain.answers.never",
      },
      {
        id: "less-than-once-a-week",
        translationKey:
          "questions.trouble-sleeping-pain.answers.less-than-once-a-week",
      },
      {
        id: "one-to-two-times-a-week",
        translationKey:
          "questions.trouble-sleeping-pain.answers.one-to-two-times-a-week",
      },
      {
        id: "three-or-more-times-a-week",
        translationKey:
          "questions.trouble-sleeping-pain.answers.three-or-more-times-a-week",
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "wake-gasping-for-breath",
    questionTranslationKey: "questions.wake-gasping-for-breath.question",
    conditionalOn: [
      {
        condition: "sex-at-birth",
        values: ["female"],
      },
    ],
    answers: [
      {
        id: "yes",
        translationKey: "questions.wake-gasping-for-breath.answers.yes",
      },
      {
        id: "no",
        translationKey: "questions.wake-gasping-for-breath.answers.no",
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "how-often-nap",
    questionTranslationKey: "questions.how-often-nap.question",
    conditionalOn: [
      {
        condition: "sex-at-birth",
        values: ["female"],
        negate: true,
      },
    ],
    answers: [
      {
        id: "never",
        translationKey: "questions.how-often-nap.answers.never",
      },
      {
        id: "rarely",
        translationKey: "questions.how-often-nap.answers.rarely",
      },
      {
        id: "occasionally",
        translationKey: "questions.how-often-nap.answers.occasionally",
      },
      {
        id: "often",
        translationKey: "questions.how-often-nap.answers.often",
      },
      {
        id: "always",
        translationKey: "questions.how-often-nap.answers.always",
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "daytime-fatigue",
    questionTranslationKey: "questions.daytime-fatigue.question",
    conditionalOn: [
      {
        condition: "sex-at-birth",
        values: ["female"],
      },
    ],
    allowMultipleAnswers: true,
    answers: [
      {
        id: "low-energy",
        translationKey: "questions.daytime-fatigue.answers.low-energy",
      },
      {
        id: "feeling-tired",
        translationKey: "questions.daytime-fatigue.answers.feeling-tired",
      },
      {
        id: "daytime-sleepiness",
        translationKey: "questions.daytime-fatigue.answers.daytime-sleepiness",
      },
      {
        id: "waking-up-unrested",
        translationKey: "questions.daytime-fatigue.answers.waking-up-unrested",
      },
      {
        id: "none-of-the-above",
        translationKey: "questions.daytime-fatigue.answers.none-of-the-above",
      },
    ],
  },
  {
    questionType: QuestionType.TRANSITION,
    questionId: "productivity-impact",
    questionTranslationKey: "transitions.productivity-impact.title",
    overlineTranslationKey: "transitions.productivity-impact.overline",
    contentBlocks: [
      {
        type: TransitionBlockType.RICH_TEXT,
        translationKey: "transitions.productivity-impact.body-1",
      },
      {
        type: TransitionBlockType.RICH_TEXT,
        translationKey: "transitions.productivity-impact.body-2",
      },
      {
        type: TransitionBlockType.IMAGE,
        imageKey: "woman-looking-at-computer",
        altTranslationKey: "transitions.productivity-impact.image-alt",
      },
      {
        type: TransitionBlockType.SOURCE,
        translationKey: "transitions.productivity-impact.source",
      },
    ],
    variants: [
      {
        variantKey: "female",
        questionTranslationKey: "transitions.productivity-impact.female.title",
        contentBlocks: [
          {
            type: TransitionBlockType.RICH_TEXT,
            translationKey: "transitions.productivity-impact.female.body-1",
          },
          {
            type: TransitionBlockType.RICH_TEXT,
            translationKey: "transitions.productivity-impact.female.body-2",
          },
          {
            type: TransitionBlockType.IMAGE,
            imageKey: "woman-looking-at-computer",
            altTranslationKey:
              "transitions.productivity-impact.female.image-alt",
          },
          {
            type: TransitionBlockType.SOURCE,
            translationKey: "transitions.productivity-impact.female.source",
          },
        ],
        conditions: [{ questionId: "sex-at-birth", answerId: "female" }],
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "restless-legs",
    questionTranslationKey: "questions.restless-legs.question",
    answers: [
      {
        id: "yes",
        translationKey: "questions.restless-legs.answers.yes",
      },
      {
        id: "no",
        translationKey: "questions.restless-legs.answers.no",
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "sleep-or-mental-health-conditions",
    questionTranslationKey:
      "questions.sleep-or-mental-health-conditions.question",
    allowMultipleAnswers: true,
    answers: [
      {
        id: "sleep-apnea",
        translationKey:
          "questions.sleep-or-mental-health-conditions.answers.sleep-apnea",
      },
      {
        id: "insomnia",
        translationKey:
          "questions.sleep-or-mental-health-conditions.answers.insomnia",
      },
      {
        id: "restless-leg-syndrome",
        translationKey:
          "questions.sleep-or-mental-health-conditions.answers.restless-leg-syndrome",
      },
      {
        id: "fragmented-sleep",
        translationKey:
          "questions.sleep-or-mental-health-conditions.answers.fragmented-sleep",
      },
      {
        id: "bruxism",
        translationKey:
          "questions.sleep-or-mental-health-conditions.answers.bruxism",
      },
      {
        id: "circadian-rhythm-disorder",
        translationKey:
          "questions.sleep-or-mental-health-conditions.answers.circadian-rhythm-disorder",
      },
      {
        id: "depression",
        translationKey:
          "questions.sleep-or-mental-health-conditions.answers.depression",
      },
      {
        id: "anxiety",
        translationKey:
          "questions.sleep-or-mental-health-conditions.answers.anxiety",
      },
      {
        id: "other",
        translationKey:
          "questions.sleep-or-mental-health-conditions.answers.other",
      },
      {
        id: "prefer-not-to-say",
        translationKey:
          "questions.sleep-or-mental-health-conditions.answers.prefer-not-to-say",
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "menopausal-status",
    questionTranslationKey: "questions.menopausal-status.question",
    conditionalOn: [
      {
        condition: "sex-at-birth",
        values: ["female"],
      },
    ],
    answers: [
      {
        id: "pre-menopausal",
        translationKey: "questions.menopausal-status.answers.pre-menopausal",
        descriptionTranslationKey:
          "questions.menopausal-status.answers.pre-menopausal-description",
      },
      {
        id: "peri-menopausal",
        translationKey: "questions.menopausal-status.answers.peri-menopausal",
        descriptionTranslationKey:
          "questions.menopausal-status.answers.peri-menopausal-description",
      },
      {
        id: "post-menopausal-surgery",
        translationKey:
          "questions.menopausal-status.answers.post-menopausal-surgery",
        descriptionTranslationKey:
          "questions.menopausal-status.answers.post-menopausal-surgery-description",
      },
      {
        id: "post-menopausal",
        translationKey: "questions.menopausal-status.answers.post-menopausal",
        descriptionTranslationKey:
          "questions.menopausal-status.answers.post-menopausal-description",
      },
      {
        id: "not-sure",
        translationKey: "questions.menopausal-status.answers.not-sure",
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "sleep-motivation",
    questionTranslationKey: "questions.sleep-motivation.question",
    allowMultipleAnswers: true,
    answers: [
      {
        id: "partner-snoring",
        translationKey: "questions.sleep-motivation.answers.partner-snoring",
      },
      {
        id: "low-energy-fatigue",
        translationKey: "questions.sleep-motivation.answers.low-energy-fatigue",
      },
      {
        id: "ongoing-health-issues",
        translationKey:
          "questions.sleep-motivation.answers.ongoing-health-issues",
      },
      {
        id: "doctors-recommendation",
        translationKey:
          "questions.sleep-motivation.answers.doctors-recommendation",
      },
      {
        id: "work-productivity",
        translationKey: "questions.sleep-motivation.answers.work-productivity",
      },
      {
        id: "sudden-event",
        translationKey: "questions.sleep-motivation.answers.sudden-event",
      },
      {
        id: "other",
        translationKey: "questions.sleep-motivation.answers.other",
      },
    ],
  },
  {
    questionType: QuestionType.MULTIPLE_CHOICE,
    questionId: "cpap-therapy-status",
    questionTranslationKey: "questions.cpap-therapy-status.question",
    allowMultipleAnswers: true,
    answers: [
      {
        id: "diagnosed-but-never-started",
        translationKey:
          "questions.cpap-therapy-status.answers.diagnosed-but-never-started",
      },
      {
        id: "started-but-no-longer-use",
        translationKey:
          "questions.cpap-therapy-status.answers.started-but-no-longer-use",
      },
      {
        id: "currently-using",
        translationKey: "questions.cpap-therapy-status.answers.currently-using",
      },
      {
        id: "none-of-the-above",
        translationKey: "questions.cpap-therapy-status.answers.none-of-the-above",
        exclusive: true,
      },
    ],
  },
  {
    questionType: QuestionType.SUBMIT,
    questionId: "submit",
    questionTranslationKey: "questions.submit.question",
    bodyTextTranslationKey: "questions.submit.body",
  },
];
